import { needsRefresh, refreshAuth } from './auth';
import { clearAuthStorage } from './sessionStorage';
import type { FetchWrapperOptions } from '../../types';

// Use environment variable, fallback to localhost
const portal_endpoint = import.meta.env.VITE_PORTAL_API_ENDPOINT || 'https://portal-dev.api.hotmic.io';
console.log('PORTAL ENDPOINT:', portal_endpoint, 'from env:', import.meta.env.VITE_PORTAL_API_ENDPOINT);

let refreshPromise: Promise<any> | null = null;

// Helper for tests to reset module state
export const __resetRefreshPromise = () => {
  refreshPromise = null;
};

// Add list of public endpoints that don't require auth
const PUBLIC_ENDPOINTS = [
  'auth/login',
  'auth/oauth/login',
  'auth/password/reset',
  'admin/theme', // Theme endpoints (singular)
  'admin/themes', // Theme endpoints (plural, includes admin/themes/subdomain)
];

// Helper function to check if current access token is still valid
const isCurrentTokenValid = (): boolean => {
  const expiresAt = localStorage.getItem('access_token_expires_at');
  if (!expiresAt) return false;

  const expiresAtTimestamp = parseInt(expiresAt, 10);
  const currentTime = Math.floor(Date.now() / 1000);

  // Simple check - token is valid if it hasn't expired yet
  return expiresAtTimestamp > currentTime;
};

export const fetchWrapperPortal = async <T = any>(args: FetchWrapperOptions): Promise<T> => {
  try {
    // Check if endpoint requires auth
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some(endpoint => args.url.startsWith(endpoint));

    // Only check for refresh if not a public endpoint and not a refresh request
    if (!isPublicEndpoint && args.url !== 'auth/refresh/' && needsRefresh()) {
      // Try to refresh proactively, but don't block or fail on it
      if (!refreshPromise) {
        refreshPromise = refreshAuth()
          .then(result => {
            refreshPromise = null;
            if (result) {
            }
            return result;
          })
          .catch(error => {
            refreshPromise = null;
            console.warn('Background token refresh failed');
            // Rethrow so calling code can handle it
            throw error;
          });
      }

      // Don't await the refresh - let it happen in background
      // Only await if the current token is actually expired
      if (!isCurrentTokenValid()) {
        try {
          await refreshPromise;
        } catch (error) {
          console.warn('Refresh failed and token expired');
          // Only logout if token is truly expired and can't be refreshed
          const expiresAt = localStorage.getItem('access_token_expires_at');
          const currentTime = Math.floor(Date.now() / 1000);

          if (!expiresAt || parseInt(expiresAt) < currentTime) {
            console.error('Token is expired and refresh failed - logging out');
            clearAuthStorage();
            if (!window.location.pathname.includes('/auth/login')) {
              window.location.href = '/auth/login';
            }
            throw error;
          }
        }
      }
      // If token is still valid, don't wait for refresh - proceed immediately
    }

    const fetchObj: RequestInit = {};
    fetchObj.headers = args.headers || {};
    fetchObj.method = args.method.toUpperCase();
    fetchObj.body = args.body ? args.body : null;

    // Only set Content-Type for JSON requests, not for FormData
    // Let browser set the Content-Type with boundary for multipart/form-data
    if (!args.isFormData) {
      (fetchObj.headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    (fetchObj.headers as Record<string, string>)['ApiKey'] = import.meta.env.VITE_APP_KEY;

    // Add auth header if token exists, even for public endpoints
    if (!(fetchObj.headers as Record<string, string>).Authorization && args.addBearer) {
      const token = args.token || localStorage.getItem('access_token');
      if (token) {
        (fetchObj.headers as Record<string, string>).Authorization = args.token ? args.token : `Bearer ${token}`;
      } else if (!isPublicEndpoint) {
        throw new Error('No authorization token available');
      }
    }

    if (args.signal) {
      fetchObj.signal = args.signal;
    }

    const result = await fetch(`${portal_endpoint}/${args.url}`, fetchObj);
    // Handle 401 responses only for non-public endpoints (403 is permission issue, not auth issue)
    if (!isPublicEndpoint && result.status === 401) {
      // Just log the error and throw - don't automatically logout
      // Let the calling code decide what to do
      console.warn(`401 Auth error for ${args.url} - token may be expired`);
      throw new Error(`Authentication error: ${result.status}`);
    }

    // Additional check for 403 errors on public endpoints
    if (isPublicEndpoint && result.status === 403) {
      throw new Error(`Forbidden: You don't have permission to access this resource (403)`);
    }

    // Handle 204 No Content
    if (result.status === 204) {
      return result.status as any;
    }

    // Check if response is JSON
    const contentType = result.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Handle 200 with empty body (no content-type)
      const text = await result.text();
      if (result.status === 200 && (!text || text.trim() === '')) {
        // Return success object for empty 200 responses
        return { success: true } as any;
      }

      console.error('Non-JSON response received:', {
        contentType,
        status: result.status,
        statusText: result.statusText,
      });
      console.error('Response text:', text);
      throw new Error(`Expected JSON response but got ${contentType}`);
    }

    const jsonResponse = await result.json();

    // Handle error responses (4xx and 5xx status codes)
    if (!result.ok) {
      // Backend returns { error: string, code: string, details?: any }
      const errorMessage = jsonResponse.error || jsonResponse.message || `Request failed with status ${result.status}`;
      const error = new Error(errorMessage) as Error & { code?: string; status?: number; details?: unknown };
      error.code = jsonResponse.code;
      error.status = result.status;
      error.details = jsonResponse.details;
      throw error;
    }

    return jsonResponse;
  } catch (e) {
    console.error(`HTTP ERROR:`, e);
    // Rethrow all errors consistently
    throw e;
  }
};
